import React, { useEffect, useRef, useState } from 'react';
import { GripVertical, Pencil, Trash2, ChevronDown, ChevronRight, Ban } from 'lucide-react';
import Badge from './Badge';
import { formatCategoryName } from './formatCategoryName';
import { useCloseOnBack } from './backLayer/useCloseOnBack';
import './CategoryHeader.css';

/**
 * Baustein — CategoryHeader.
 * Kopfzeile einer Karten-Kategorie im Deck-Editor (alle vier Ansichten nutzen
 * denselben Baustein). Rein präsentational: DnD (Sortable/Droppable), Confirm-
 * Dialoge und Persistenz liegen beim Konsumenten und kommen per Props rein.
 *
 * Layout: [Griff] [Name|Rename-Input] [✎] [Nicht-im-Deck] ····· [Preis] [Anzahl] [Chevron] [🗑]
 *   - Anzahl: immer sichtbar (Zähler-Pille; bei `excluded` mit ⊘-Icon + Tooltip).
 *   - Breitenabhängig (Container-Query): unter 300px erscheinen Preis, Stift und
 *     Mülleimer erst bei Header-Hover (der Preis tauscht dabei mit dem Zähler);
 *     ab 300px sind Preis + Mülleimer permanent sichtbar (fest reservierter
 *     Platz — kein Chevron-Verrutschen unterm Mauszeiger); der Stift bleibt
 *     auch dann nur bei Hover/Fokus sichtbar.
 *   - `excluded` („Nicht im Deck"-Kategorie): gestrichelter Rahmen + ⊘-Zähler;
 *     das Text-Badge „Nicht im Deck" erscheint erst ab 360px Containerbreite
 *     (darunter würde es den Namen zerquetschen) — bis dahin trägt die
 *     ⊘-Zähler-Pille allein die Kennzeichnung.
 *   - DnD (Griff, Drop-Target-Hervorhebung) kommt komplett per Props vom
 *     Konsumenten — der Baustein selbst kennt kein DnD-Framework.
 *   - `stickyTop` rechnet `--app-header-offset` und `--profile-banner-offset`
 *     ein, weil der App-Header auf Mobile über der Werkzeugleiste klebt und
 *     der ProfileBanner (Fremd-Profil) zusätzlich darüber; Fallback `0px`
 *     hält den Baustein als Leaf-Library-Komponente auch ohne diese
 *     App-Chrome-Variablen eigenständig lauffähig (z.B. in der `/design`-Showcase).
 *   - `menu` (optional, ReactNode): rendert einen zusätzlichen Slot rechts außen,
 *     nach dem Mülleimer. Der Konsument übergibt ein fertiges `ui/ContextMenu`
 *     (⋮-Trigger) — der Baustein kennt dessen Inhalt nicht.
 *   - `renameRequestId` (optional): programmatischer Rename-Einstieg für Konsumenten
 *     ohne eigenen Pencil-Button (z.B. ein Menüpunkt „Umbenennen" in `menu`). Ein
 *     gegenüber dem letzten Wert geänderter Wert versetzt den Header in den
 *     Edit-Modus, unabhängig von `canRename`. Default `null` ändert nichts am
 *     bisherigen Verhalten.
 *   - Am Handy bricht auch die Zurück-Geste das Umbenennen ab (wie Escape).
 *
 * Verwendung:
 *   <CategoryHeader name="Creatures" count={12} price="34,56 €"
 *     canRename onRename={(n) => …} canRemove onRemove={…}
 *     dragHandle dragListeners={listeners} isDropTarget={isOver} stickyTop={0} />
 */
export default function CategoryHeader({
  name,
  count,
  price = null,
  excluded = false,
  collapsible = false,
  collapsed = false,
  onToggleCollapse,
  canRename = false,
  onRename,
  canRemove = false,
  onRemove,
  dragHandle = false,
  dragListeners,
  isDropTarget = false,
  stickyTop,
  menu = null,
  renameRequestId = null,
}) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef(null);
  const lastRenameRequestIdRef = useRef(renameRequestId);

  useEffect(() => {
    if (isRenaming) inputRef.current?.select();
  }, [isRenaming]);

  useEffect(() => {
    if (renameRequestId == null || renameRequestId === lastRenameRequestIdRef.current) return;
    lastRenameRequestIdRef.current = renameRequestId;
    setDraft(name);
    setIsRenaming(true);
  }, [renameRequestId, name]);

  const startRename = (e) => {
    e.stopPropagation();
    setDraft(name);
    setIsRenaming(true);
  };
  const submitRename = () => {
    setIsRenaming(false);
    const next = draft.trim();
    if (next && next !== name) onRename?.(next);
  };
  const cancelRename = () => {
    setIsRenaming(false);
    setDraft(name);
  };

  // `cancelRename` unmountet das Feld, React feuert dabei kein `blur` — ein zusätzlicher
  // Vermerk gegen das Speichern-beim-Verlassen ist hier anders als beim Decknamen nicht nötig.
  useCloseOnBack(isRenaming, cancelRename);

  const cls = [
    'ui-category-header',
    isDropTarget && 'ui-category-header--drop-target',
    excluded && 'ui-category-header--excluded',
    dragHandle && 'ui-category-header--draggable',
    price != null && 'ui-category-header--has-price',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      style={
        stickyTop != null
          ? {
              position: 'sticky',
              // Der App-Header klebt auf Mobile über der Werkzeugleiste, der
              // ProfileBanner (Fremd-Profil) zusätzlich darüber; beide Offsets
              // sind 0px, solange sie nicht gerendert werden.
              top: `calc(var(--app-header-offset, 0px) + var(--profile-banner-offset, 0px) + ${stickyTop}px)`,
              zIndex: 40,
            }
          : undefined
      }
      {...(isRenaming ? {} : dragListeners)}
    >
      {dragHandle && (
        <GripVertical size={14} aria-hidden className="ui-category-header__grip" />
      )}

      {isRenaming ? (
        <input
          ref={inputRef}
          className="ui-category-header__rename-input"
          aria-label="Kategorienamen bearbeiten"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={submitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitRename();
            if (e.key === 'Escape') cancelRename();
          }}
          onPointerDown={(e) => e.stopPropagation()}
        />
      ) : (
        <span className="ui-category-header__name" title={name}>{formatCategoryName(name)}</span>
      )}

      {canRename && !isRenaming && (
        <button
          type="button"
          className="ui-category-header__icon-btn ui-category-header__rename-btn"
          aria-label="Kategorie umbenennen"
          title="Kategorie umbenennen"
          onClick={startRename}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Pencil size={13} aria-hidden />
        </button>
      )}

      {excluded && (
        <span
          className="ui-category-header__excluded"
          title="Karten dieser Kategorie zählen nicht zum Deck"
        >
          <Badge tone="pending">Nicht im Deck</Badge>
        </span>
      )}

      <span className="ui-category-header__spacer" />

      {/* Kennzahlen rechtsbündig hinter dem Spacer: der bei Hover erscheinende
          Preis nimmt Platz vom leeren Spacer, nie vom Kategorienamen. */}
      {price != null && <span className="ui-category-header__price">{price}</span>}
      {/* Excluded: Zähler und Kennzeichnung verschmelzen zu einer Pille —
          „⊘ 14" heißt „14 Karten, die nicht zum Deck zählen". */}
      <span
        className="ui-category-header__count"
        title={excluded ? 'Karten dieser Kategorie zählen nicht zum Deck' : undefined}
      >
        <Badge>
          {excluded && <Ban size={11} aria-hidden className="ui-category-header__count-icon" />}
          {count}
        </Badge>
      </span>

      {collapsible && (
        <button
          type="button"
          className="ui-category-header__icon-btn"
          aria-label={collapsed ? 'Kategorie ausklappen' : 'Kategorie einklappen'}
          title={collapsed ? 'Ausklappen' : 'Einklappen'}
          onClick={(e) => { e.stopPropagation(); onToggleCollapse?.(); }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {collapsed ? <ChevronRight size={15} aria-hidden /> : <ChevronDown size={15} aria-hidden />}
        </button>
      )}

      {canRemove && (
        <button
          type="button"
          className="ui-category-header__icon-btn ui-category-header__remove-btn"
          aria-label="Kategorie entfernen"
          title="Kategorie entfernen"
          onClick={(e) => { e.stopPropagation(); onRemove?.(); }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Trash2 size={14} aria-hidden />
        </button>
      )}

      {menu ? <span className="category-header__menu">{menu}</span> : null}
    </div>
  );
}
